import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [role, setRole] = useState(null); // 'admin' o 'tech'
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Controlla se c'è una sessione salvata
    const savedSession = localStorage.getItem('adminSession');
    const savedRole = localStorage.getItem('adminRole');
    const savedPermissions = localStorage.getItem('adminPermissions');
    
    if (savedSession && savedRole) {
      setIsAuthenticated(true);
      setRole(savedRole);
      setPermissions(savedPermissions ? JSON.parse(savedPermissions) : {});
    }
    setLoading(false);
  }, []);

  const login = async (password) => {
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      const { success, role: userRole, permissions: userPermissions } = response.data;
      
      if (success) {
        setIsAuthenticated(true);
        setRole(userRole);
        setPermissions(userPermissions || {});
        
        localStorage.setItem('adminSession', 'true');
        localStorage.setItem('adminRole', userRole);
        localStorage.setItem('adminPassword', password);
        localStorage.setItem('adminPermissions', JSON.stringify(userPermissions || {}));
        
        return { success: true, role: userRole };
      }
      return { success: false, error: 'Login fallito' };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.response?.data?.detail || 'Password errata!' };
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    setRole(null);
    setPermissions({});
    localStorage.removeItem('adminSession');
    localStorage.removeItem('adminRole');
    localStorage.removeItem('adminPassword');
    localStorage.removeItem('adminPermissions');
  };

  // Helper per verificare permessi specifici
  const hasPermission = (permission) => {
    return permissions[permission] === true;
  };

  // Backward compatibility: isAdmin è true se autenticato (qualsiasi ruolo)
  const isAdmin = isAuthenticated;

  return (
    <AdminContext.Provider value={{ 
      isAdmin, 
      isAuthenticated,
      role, 
      permissions,
      hasPermission,
      login, 
      logout, 
      loading 
    }}>
      {children}
    </AdminContext.Provider>
  );
};
